"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { RestrictionMap } from "@/components/tools/biology-genetics/restriction-map";
import {
  EnzymeTable,
  FragmentTable,
  SiteTable,
} from "@/components/tools/biology-genetics/restriction-tables";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { PUC19 } from "@/lib/tool-kit/biology-genetics/examples";
import { parseFasta } from "@/lib/tool-kit/biology-genetics/fasta";
import { cleanNucleotides } from "@/lib/tool-kit/biology-genetics/iupac";
import {
  ALL_ENZYMES,
  type Enzyme,
  type EnzymeResult,
  fragments,
  makeEnzyme,
  POPULAR,
  searchAll,
} from "@/lib/tool-kit/biology-genetics/restriction";

/** English strings. A translated content file overrides these through `ui`. */
export const RESTRICTION_SITE_FINDER_STRINGS = {
  inputLabel: "DNA sequence — plain text or FASTA",
  inputHint:
    "The first FASTA record is searched. Spaces, line breaks, numbers and lower case are fine; U is read as T. Up to 100,000 bases.",
  inputPlaceholder: ">my_plasmid\nGAATTCGGATCC…",
  exampleHeader: "L09137.2 Cloning vector pUC19c, complete sequence",
  tryExample: "Try an example (pUC19)",
  shapeLabel: "Sequence shape",
  linear: "Linear",
  circular: "Circular (plasmid)",
  setLabel: "Enzymes to test",
  setPopular: "Popular cloning enzymes",
  setAll: "All NEB enzymes",
  showLabel: "Show",
  showSingle: "Single cutters",
  showCutters: "All enzymes that cut",
  showNone: "Enzymes that do not cut",
  showAll: "Every enzyme tested",
  customHeading: "Add your own enzyme (optional)",
  customName: "Name",
  customSite: "Recognition site",
  customHint:
    "REBASE notation: G^AATTC puts the cut after the G; GGTCTC(1/5) cuts 1 base beyond the site on the top strand and 5 on the bottom. IUPAC codes are allowed.",
  customDefaultName: "Custom",
  errorCustom:
    "That recognition site is not valid. Use letters from ACGTRYSWKMBDHVN with a ^ or a (n/n) cut.",
  results: "Results",
  statLength: "Sequence length",
  statTested: "Enzymes tested",
  statCutting: "Enzymes that cut",
  statSingle: "Single cutters",
  unitBp: "bp",
  enzymeTableHeading: "Enzymes",
  selectHint: "Tick enzymes to digest with them.",
  selectColumn: "Digest",
  selectEnzyme: "Digest with {name}",
  enzymeColumn: "Enzyme",
  siteColumn: "Recognition site",
  cutsColumn: "Cuts",
  positionsColumn: "Cut after base",
  endsColumn: "Ends",
  morePositions: "(+{count} more)",
  blunt: "Blunt",
  overhang5: "5′ overhang, {length} nt",
  overhang3: "3′ overhang, {length} nt",
  noEnzymes: "No enzymes match this view.",
  digestHeading: "Digest",
  digestWith: "Digest with {enzymes}",
  noneSelected:
    "Tick one or more enzymes above to see the fragments, map and sites.",
  fragmentsHeading: "Fragments",
  fragmentColumn: "#",
  fromColumn: "From base",
  toColumn: "To base",
  sizeColumn: "Size (bp)",
  fragmentSummary: "one: {count} fragment|other: {count} fragments",
  copyFragments: "Copy fragment sizes",
  copied: "Copied!",
  sitesHeading: "Sites",
  strandColumn: "Strand",
  siteStartColumn: "Site start",
  siteEndColumn: "Site end",
  cutAfterColumn: "Cut after base",
  matchedColumn: "Matched bases",
  mapHeading: "Map",
  mapLabel: "Restriction map of the sequence",
  mapDescription:
    "Map of {length} bases marking the cut positions of {enzymes}.",
  highlightLabel:
    "Start of the sequence with the selected enzymes' sites highlighted",
  truncated: "Showing the first {count} bases only.",
  prompt: "Paste a DNA sequence to find restriction sites.",
  errorNone: "No nucleotide letters were found in that text.",
  ignored: "Ignored characters that are not nucleotide codes: {list}.",
  tooLong: "Only the first {count} bases are searched.",
  sitesNote:
    "A cut falls after the numbered base on the top strand; fragment sizes use those positions. Sites on the bottom strand are listed with strand −.",
} as const;

type Shape = "linear" | "circular";
type SetChoice = "popular" | "all";
type Show = "single" | "cutters" | "none" | "all";

const MAX_BASES = 100_000;
const DISPLAY_LIMIT = 4000;
const TONES = ["amber", "sky", "rose", "emerald", "violet"] as const;

function filterResults(
  results: readonly EnzymeResult[],
  show: Show
): EnzymeResult[] {
  const keep = {
    single: (r: EnzymeResult) => r.hits.length === 1,
    cutters: (r: EnzymeResult) => r.hits.length > 0,
    none: (r: EnzymeResult) => r.hits.length === 0,
    all: () => true,
  }[show];
  return results.filter(keep);
}

export default function RestrictionSiteFinderWidget() {
  const strings = useToolUi(RESTRICTION_SITE_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState(`>${strings.exampleHeader}\n${PUC19}`);
  const [shape, setShape] = useState<Shape>("circular");
  const [setChoice, setSetChoice] = useState<SetChoice>("popular");
  const [show, setShow] = useState<Show>("single");
  const [customName, setCustomName] = useState("");
  const [customSite, setCustomSite] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    new Set(["EcoRI", "HindIII"])
  );
  const deferredText = useDeferredValue(text);

  const record = parseFasta(deferredText)[0];
  const cleaned = cleanNucleotides(record?.sequence ?? "");
  const sequence = cleaned.sequence.slice(0, MAX_BASES).replaceAll("U", "T");
  const circular = shape === "circular";
  const custom: Enzyme | null =
    customSite.trim() === ""
      ? null
      : makeEnzyme(customName.trim() || strings.customDefaultName, customSite);
  const customInvalid = customSite.trim() !== "" && custom === null;
  const enzymes = useMemo(() => {
    const base = setChoice === "popular" ? POPULAR : ALL_ENZYMES;
    return custom
      ? [custom, ...base.filter((e) => e.name !== custom.name)]
      : [...base];
  }, [setChoice, custom]);
  const results = useMemo(
    () => (sequence === "" ? [] : searchAll(sequence, enzymes, circular)),
    [sequence, enzymes, circular]
  );

  const shown = filterResults(results, show);
  const chosen = results.filter(
    (r) => selected.has(r.enzyme.name) && r.hits.length > 0
  );
  const hits = chosen
    .flatMap((r) => r.hits)
    .sort((a, b) => a.topCut - b.topCut);
  const pieces = fragments(
    hits.map((h) => h.topCut),
    sequence.length,
    circular
  );
  const chosenNames = chosen.map((r) => r.enzyme.name).join(", ");

  function toggle(name: string, on: boolean) {
    setSelected((previous) => {
      const next = new Set(previous);
      if (on) {
        next.add(name);
      } else {
        next.delete(name);
      }
      return next;
    });
  }

  const matches: TextMatch[] = chosen.flatMap((r, index) =>
    r.hits
      .filter((hit) => hit.end <= DISPLAY_LIMIT)
      .map((hit) => ({
        start: hit.start,
        end: hit.end,
        category: String(index % TONES.length),
        title: hit.enzyme,
      }))
  );
  const tones = Object.fromEntries(
    TONES.map((tone, index) => [String(index), tone])
  );
  const colorOf = new Map(chosen.map((r, index) => [r.enzyme.name, index]));
  const cutting = results.filter((r) => r.hits.length > 0).length;
  const single = results.filter((r) => r.hits.length === 1).length;

  let message: string | null = null;
  if (text.trim() === "") {
    message = strings.prompt;
  } else if (sequence === "") {
    message = strings.errorNone;
  }

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="rsf-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={6}
        value={text}
      />
      <button
        className={BUTTON_SECONDARY}
        onClick={() => setText(`>${strings.exampleHeader}\n${PUC19}`)}
        type="button"
      >
        {strings.tryExample}
      </button>
      <div className={`${CARD} space-y-4`}>
        <div className="grid gap-4 sm:grid-cols-2">
          <PillGroup
            label={strings.shapeLabel}
            name="rsf-shape"
            onChange={setShape}
            options={[
              { value: "linear", label: strings.linear },
              { value: "circular", label: strings.circular },
            ]}
            value={shape}
          />
          <SelectField
            id="rsf-set"
            label={strings.setLabel}
            onChange={setSetChoice}
            options={[
              { value: "popular", label: strings.setPopular },
              { value: "all", label: strings.setAll },
            ]}
            value={setChoice}
          />
        </div>
        <details>
          <summary className="cursor-pointer font-medium text-foreground text-sm">
            {strings.customHeading}
          </summary>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <TextField
              id="rsf-custom-name"
              label={strings.customName}
              onChange={setCustomName}
              value={customName}
            />
            <TextField
              hint={strings.customHint}
              id="rsf-custom-site"
              label={strings.customSite}
              mono
              onChange={setCustomSite}
              value={customSite}
            />
          </div>
        </details>
      </div>
      {customInvalid ? <ErrorNotice>{strings.errorCustom}</ErrorNotice> : null}
      {cleaned.invalid.length > 0 ? (
        <Notice>
          {t(strings.ignored, { list: cleaned.invalid.join(" ") })}
        </Notice>
      ) : null}
      {cleaned.sequence.length > MAX_BASES ? (
        <Notice>{t(strings.tooLong, { count: fmt(MAX_BASES) })}</Notice>
      ) : null}
      {message ? <Notice>{message}</Notice> : null}
      {sequence === "" ? null : (
        <div aria-live="polite" className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.statLength}
              value={`${fmt(sequence.length)} ${strings.unitBp}`}
            />
            <StatCard
              label={strings.statCutting}
              value={`${fmt(cutting)} / ${fmt(results.length)}`}
            />
            <StatCard label={strings.statSingle} value={fmt(single)} />
          </StatGrid>
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.enzymeTableHeading}</h3>
            <SelectField
              id="rsf-show"
              label={strings.showLabel}
              onChange={setShow}
              options={[
                { value: "single", label: strings.showSingle },
                { value: "cutters", label: strings.showCutters },
                { value: "none", label: strings.showNone },
                { value: "all", label: strings.showAll },
              ]}
              value={show}
            />
            <p className={HINT}>{strings.selectHint}</p>
            {shown.length === 0 ? (
              <Notice>{strings.noEnzymes}</Notice>
            ) : (
              <EnzymeTable
                fmt={fmt}
                onToggle={toggle}
                results={shown}
                selected={selected}
                strings={strings}
              />
            )}
          </div>
          <div className="space-y-4">
            <h3 className={WIDGET_H3}>{strings.digestHeading}</h3>
            {chosen.length === 0 ? (
              <Notice>{strings.noneSelected}</Notice>
            ) : (
              <>
                <p className="font-medium text-foreground text-sm">
                  {t(strings.digestWith, { enzymes: chosenNames })}
                </p>
                <div className="space-y-2">
                  <h4 className="font-medium text-foreground text-sm">
                    {strings.mapHeading}
                  </h4>
                  <RestrictionMap
                    circular={circular}
                    cuts={hits.map((hit) => ({
                      enzyme: hit.enzyme,
                      position: hit.topCut,
                      colorIndex: colorOf.get(hit.enzyme) ?? 0,
                    }))}
                    description={t(strings.mapDescription, {
                      length: fmt(sequence.length),
                      enzymes: chosenNames,
                    })}
                    id="rsf-map"
                    label={strings.mapLabel}
                    length={sequence.length}
                  />
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-foreground text-sm">
                    {strings.fragmentsHeading}
                  </h4>
                  <p className={HINT}>
                    {tPlural(strings.fragmentSummary, locale, pieces.length, {
                      count: fmt(pieces.length),
                    }).trim()}
                  </p>
                  <FragmentTable fmt={fmt} rows={pieces} strings={strings} />
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copyFragments}
                    text={[...pieces]
                      .sort((a, b) => b.length - a.length)
                      .map((p) => p.length)
                      .join(", ")}
                  />
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-foreground text-sm">
                    {strings.sitesHeading}
                  </h4>
                  <p className={HINT}>{strings.sitesNote}</p>
                  <SiteTable fmt={fmt} hits={hits} strings={strings} />
                </div>
                <div className="space-y-2">
                  <HighlightedText
                    label={strings.highlightLabel}
                    matches={matches}
                    text={sequence.slice(0, DISPLAY_LIMIT)}
                    tones={tones}
                  />
                  {sequence.length > DISPLAY_LIMIT ? (
                    <p className={HINT}>
                      {t(strings.truncated, { count: fmt(DISPLAY_LIMIT) })}
                    </p>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
